import type { GetWebcastSummaryManagerQuery } from '../../../../../../generated/graphql-manager';

import { title } from 'process';

import {
  CheckIcon,
  DuplicateIcon,
  Heading,
  IconButton,
  InfoCircleIcon,
  InventoryIcon,
  Paragraph,
  PlayerIcon,
  PrimaryButton,
  Spinner,
  Tooltip,
} from '@movingimage-evp/mi-ui-component-library';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { ArchiveWebcastModal } from 'src/manager/pages/webcasts/archive-webcast';
import { UnarchiveWebcastModal } from 'src/manager/pages/webcasts/unarchive-webcast';

import { State, useEnableVodMutation } from '../../../../../../generated/graphql-manager';
import { useAbsoluteRoutes } from '../../../../../../routes';
import { addParamToPath, formatEventStartDate } from '../../../../../../utils';
import { Languages } from '../../../../../components/language';
import { useUserPermissions } from '../../../../../hooks/user-permissions';
import { DuplicateWebcastModal, DuplicationProgressIndicator } from '../../../../webcasts/duplicate-webcast';
import { EnableVodPopup } from '../enabled-vod-popup/enable-vod-popup';

import styles from './event-summary-header.module.css';

type EventSummaryHeaderProps = {
  webcast: GetWebcastSummaryManagerQuery['webcast'];
  webcastName: string;
  session?: GetWebcastSummaryManagerQuery['session'];
  onEventArchiveProcess: () => void;
  onVideoSelect: () => void;
};

const PROGRESS_INDICATOR_TIMEOUT = 5000;

export function EventSummaryHeader({
  webcast,
  webcastName,
  session,
  onEventArchiveProcess,
  onVideoSelect,
}: EventSummaryHeaderProps) {
  const navigate = useNavigate();
  const routes = useAbsoluteRoutes();

  const [isDuplicateModalVisible, setIsDuplicateModalVisible] = useState(false);
  const [isProgressIndicatorVisible, setIsProgressIndicatorVisible] = useState(false);
  const [isArchiveModalVisible, setIsArchiveModalVisible] = useState(false);
  const [isUnarchiveModalVisible, setIsUnarchiveModalVisible] = useState(false);
  const [showEnableVodPopup, setShowEnableVodPopup] = useState(false);

  const [enableVodMutation, { loading: enablingVod }] = useEnableVodMutation();

  const timeoutIdRef = useRef<number>(undefined);
  const errorTimeoutIdRef = useRef<number>(undefined);
  const { isEventDuplicationAllowed, isEventArchivingAllowed } = useUserPermissions();
  const isArchivedWebcast = webcast.state === State.ARCHIVED;

  const webcastId = webcast.id;
  const primaryLanguage = webcast?.primaryLang;
  const additionalLanguages = webcast?.additionalLangs || [];
  const languages = [primaryLanguage, ...additionalLanguages];
  const streaming = webcast?.streaming;
  const vodVideos = streaming?.vod?.streams;
  const vodEnabled = streaming?.vod?.enabled;
  const areVideosChoosen =
    vodVideos?.length === languages.length &&
    vodVideos.every((vodVideo) => vodVideo?.reference?.videoId || vodVideo.videoVodManifestUrl);

  const duplicateTooltipText = isEventDuplicationAllowed
    ? t('manager.eventSummary.duplicateButton')
    : t('manager.webcastSetup.notEditableMessage.role');

  const handleWebcastDuplicatedError = () => {
    errorTimeoutIdRef.current = window.setTimeout(() => {
      setIsProgressIndicatorVisible(false);
    }, PROGRESS_INDICATOR_TIMEOUT);
  };

  const handleWebcastDuplicated = () => {
    timeoutIdRef.current = window.setTimeout(() => {
      setIsProgressIndicatorVisible(false);
    }, PROGRESS_INDICATOR_TIMEOUT);
  };

  const archiveTooltipText = isArchivedWebcast
    ? t('manager.eventSummary.unarchiveButton')
    : t('manager.eventSummary.archiveButton');

  const handleWebcastArchiveProcess = () => {
    if (isArchivedWebcast) {
      setIsUnarchiveModalVisible(true);
    } else {
      setIsArchiveModalVisible(true);
    }
  };

  const handleEnableVod = () => {
    setShowEnableVodPopup(false);

    enableVodMutation({
      variables: { input: { webcastId } },
      onCompleted: onVideoSelect,
    });
  };

  const startDate = session?.__typename === 'GetSessionSuccess' ? session.startDate : undefined;

  useEffect(
    () => () => {
      clearTimeout(timeoutIdRef.current);
      clearTimeout(errorTimeoutIdRef.current);
    },
    []
  );

  return (
    <>
      <div className={styles.headerContainer}>
        <div className={styles.eventInfo}>
          {title && (
            <Heading data-testid="event-name" style={{ color: 'var(--mi-neutral-100)' }}>
              {webcastName}
            </Heading>
          )}

          <div className={styles.eventDetails}>
            <div className={styles.eventRow}>
              <Paragraph>{t('manager.eventSummary.time')}:</Paragraph>
              <Paragraph style={{ color: 'var(--mi-neutral-100)' }}>
                {startDate && formatEventStartDate(startDate)}
              </Paragraph>
            </div>

            <div className={styles.eventRow}>
              <Paragraph>{t('manager.eventSummary.languages')}:</Paragraph>
              <span className={styles.eventValue}>
                <Languages languages={languages} />
              </span>
            </div>
          </div>
        </div>

        <div className={styles.buttonColumn}>
          <div className={styles.actionButtons}>
            <Tooltip label={duplicateTooltipText}>
              <IconButton
                disabled={!isEventDuplicationAllowed}
                aria-label={t('manager.eventSummary.duplicateButton')}
                onClick={() => setIsDuplicateModalVisible(true)}
                data-testid="duplicate-button"
                naked
              >
                <DuplicateIcon />
              </IconButton>
            </Tooltip>

            <Tooltip label={archiveTooltipText}>
              <IconButton
                disabled={!isEventArchivingAllowed}
                aria-label={archiveTooltipText}
                onClick={handleWebcastArchiveProcess}
                data-testid={isArchivedWebcast ? 'unarchive-button' : 'archive-button'}
                naked
              >
                <InventoryIcon />
              </IconButton>
            </Tooltip>

            <Tooltip label={t('manager.eventSummary.eventSummaryButton')}>
              <IconButton
                aria-label={t('manager.eventSummary.eventSummaryButton')}
                onClick={() => navigate(addParamToPath(routes.webcastSetup_setupSummary, webcast.id))}
                data-testid="setup-summary-button"
                naked
              >
                <InfoCircleIcon />
              </IconButton>
            </Tooltip>

            {!isArchivedWebcast && (
              <Tooltip label={t('manager.eventSummary.viewerButton')}>
                <IconButton
                  aria-label={t('manager.eventSummary.viewerButton')}
                  component={NavLink}
                  to={addParamToPath(routes.viewer, webcast.id)}
                  rel="noreferrer"
                  target="_blank"
                  data-testid="viewer-button"
                  naked
                >
                  <PlayerIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>

          <div className={styles.onDemandWrapper}>
            {!vodEnabled ? (
              <Tooltip
                label={
                  languages.length > 1
                    ? t('manager.eventSummary.videoOnDemand.switchToOnDemandButtonTooltip.multiLanguage')
                    : t('manager.eventSummary.videoOnDemand.switchToOnDemandButtonTooltip.singleLanguage')
                }
                hidden={areVideosChoosen}
              >
                <PrimaryButton
                  data-testid="enable-vod-button"
                  small
                  disabled={!areVideosChoosen || enablingVod}
                  onClick={() => setShowEnableVodPopup(true)}
                  className={styles.onDemandButton}
                >
                  {enablingVod ? <Spinner size={18} /> : t('manager.eventSummary.videoOnDemand.switchToOnDemandButton')}
                </PrimaryButton>
              </Tooltip>
            ) : (
              <div data-testid="vod-enabled" className={styles.vodEnabled}>
                {t('manager.eventSummary.videoOnDemand.onDemandSwitchedOn')} <CheckIcon />
              </div>
            )}
          </div>
        </div>
      </div>

      {isDuplicateModalVisible && (
        <DuplicateWebcastModal
          webcastId={webcast.id}
          webcastName={webcastName}
          onDuplicate={() => {
            setIsProgressIndicatorVisible(true);
            setIsDuplicateModalVisible(false);
          }}
          onClose={() => setIsDuplicateModalVisible(false)}
        />
      )}

      {isProgressIndicatorVisible && (
        <DuplicationProgressIndicator
          duplicatedWebcastId={webcast.id}
          duplicatedWebcastName={webcastName}
          onWebcastDuplicated={handleWebcastDuplicated}
          onWebcastDuplicatedError={handleWebcastDuplicatedError}
        />
      )}

      <ArchiveWebcastModal
        webcastId={webcast.id}
        isOpen={isArchiveModalVisible}
        onClose={() => {
          setIsArchiveModalVisible(false);
          onEventArchiveProcess();
        }}
      />

      <UnarchiveWebcastModal
        webcastId={webcast.id}
        isOpen={isUnarchiveModalVisible}
        onClose={() => {
          setIsUnarchiveModalVisible(false);
          onEventArchiveProcess();
        }}
      />

      <EnableVodPopup
        isOpen={showEnableVodPopup}
        onClose={() => setShowEnableVodPopup(false)}
        onSwitchToOnDemand={handleEnableVod}
      />
    </>
  );
}
